import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { getSummaryData, getSummaryDataFromSplitConfig } from './summaryApi'
import { createCacheKey } from '../../../utils/summary'

/**
 * Fetches summary data using React Query.
 *
 * Key improvements over legacy version:
 * 1. Only dataConfig, queryConfig, and view affect the cache key
 * 2. Style/rendering changes in viewConfig don't trigger refetches
 * 3. Proper cache key serialization (handles Maps)
 *
 * @param {Object} dataConfig - Data configuration (selectedItems, categoryAxis, etc.)
 * @param {Object} queryConfig - Query configuration (period, timeRange, etc.)
 * @param {Object} viewConfig - View configuration (view type affects API action)
 * @param {Array} prompts - Active prompts for filtering
 * @param {Object} options - Additional options
 * @returns {Object} - Query result with summaryData, isLoading, etc.
 */
export const useSummaryDataProvider = (
    dataConfig,
    queryConfig,
    viewConfig,
    prompts,
    options = {}
) => {
    const { enabled = true, staleTime = 30000 } = options

    // Create stable cache key from data, query config, and view type
    // Note: Only view type from viewConfig affects cache key, not styling
    const queryKey = useMemo(
        () => createCacheKey(dataConfig, queryConfig, viewConfig?.view),
        [dataConfig, queryConfig, viewConfig?.view]
    )

    // Determine if we should fetch
    const shouldFetch = enabled && dataConfig?.selectedItems?.length > 0

    const { data, isLoading, isFetching, error, refetch } = useQuery({
        queryKey,
        queryFn: () => getSummaryDataFromSplitConfig(dataConfig, queryConfig, viewConfig, prompts),
        enabled: shouldFetch,
        staleTime,
    })

    return {
        summaryData: data,
        isLoading,
        isFetching,
        error,
        refetch,
    }
}

/**
 * Legacy hook for backwards compatibility.
 * Uses the old single-config approach.
 *
 * @deprecated Use useSummaryDataProvider with split configs instead
 */
export const useSummaryDataProviderLegacy = (summaryConfig, prompts) => {
    const serializedConfig = summaryConfig ? JSON.stringify(summaryConfig) : null
    const serializedPrompts = prompts ? JSON.stringify(prompts) : null

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['summaryData', serializedConfig, serializedPrompts],
        queryFn: () => getSummaryData(summaryConfig, prompts),
        enabled: Boolean(summaryConfig?.selectedItems?.length),
    })

    return {
        summaryData: data,
        loadingSummaryData: isLoading,
        refetchSummaryData: refetch,
    }
}
